import React, { useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Box, Button, CircularProgress, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Unstable_Grid2";
import { MapPinSimpleArea } from "@phosphor-icons/react";

import type { GoalDomain, Status as GoalStatus } from "api/types/aba";
import type { GoalMenuState } from "./shared/types";
import EmptyState from "./shared/EmptyState";

// API hooks
import useGetListOfGoals from "hooks/api/goals/useGetListOfGoals";
import useArchiveGoal from "hooks/api/goals/useArchiveGoal";
import useUnarchiveGoal from "hooks/api/goals/useUnarchiveGoal";
import useDeleteGoal from "hooks/api/goals/useDeleteGoal";
import useGetGoalMasteryHistory from "hooks/api/goals/useGetGoalMasteryHistory";
import { useGetClientById } from "hooks/api/useGetClientById";

import GoalGridTopRow from "./Goals/GoalGridTopRow";
import GoalGridFilterRow from "./Goals/GoalGridFilterRow";
import { useGoalFilters } from "./Goals/GoalGridFilterRow/useGoalFilters";
import GoalCard from "./Goals/GoalCard";
import GoalTargetTrendChart from "./Goals/GoalTargetTrendChart";
import { ChartEmptyState } from "./shared/ChartStateCard";
import getStatusStyles from "./shared/StatusPill/getStatusColor";
import GoalActionMenu from "./Goals/GoalActionMenu";
import GoalBuilderPanel from "./Goals/GoalBuilderPanel";
import DiscontinueGoalPanel from "./Goals/DiscontinueGoalPanel";
import PutOnHoldPanel from "./Goals/PutOnHoldPanel";
import ArchiveGoalPanel from "./Goals/ArchiveGoalPanel";
import DeleteGoalPanel from "./Goals/DeleteGoalPanel";
import type { TargetActionState } from "./Goals/GoalDispositionPanel";
import {
  selectActiveTargetOptions,
  selectGoalsForMove,
  buildTargetDispositions,
} from "./utils/goalSelectors";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ActivePanel = "create" | "edit" | "discontinue" | "putOnHold" | "archive" | "delete" | null;

interface PanelState {
  active: ActivePanel;
  goalId: number | null;
}

interface GoalCardMasteryChartProps {
  goalId: number;
  status: GoalStatus;
  targetCount: number;
}

const GoalCardMasteryChart: React.FC<GoalCardMasteryChartProps> = ({
  goalId,
  status,
  targetCount,
}) => {
  const theme = useTheme();
  const { series, isMasteryHistoryLoading } = useGetGoalMasteryHistory(targetCount > 0 ? goalId : 0);
  const chartColor = getStatusStyles(theme)[status].dotColor;

  if (targetCount === 0) {
    return (
      <ChartEmptyState size="compact" title="Nothing to chart." description="" minHeight={100} />
    );
  }

  if (isMasteryHistoryLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={100}>
        <CircularProgress size={20} aria-label="Loading mastery chart" />
      </Box>
    );
  }

  // Safety net: API should always return at least Week 1, but guard anyway.
  if (!series.length) {
    return <ChartEmptyState size="compact" description="" minHeight={100} />;
  }

  const labels = series.map((point) => `W${point.week}`);
  const data = series.map((point) => point.targets_mastered);

  return (
    <GoalTargetTrendChart
      labels={labels}
      data={data}
      height={100}
      color={chartColor}
      totalTargets={targetCount}
    />
  );
};

/**
 * Main view for a client's goals grid.
 * Renders goal cards and orchestrates all goal action panels
 * (Create, Edit, Discontinue, Put On Hold, Archive, Delete).
 * Each panel owns its own form and mutation logic — this component
 * only manages which panel is open and which goal is selected.
 */
const ListOfGoals: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const history = useHistory();

  const [goalMenu, setGoalMenu] = useState<GoalMenuState>({ anchorEl: null, goalId: null });
  const [panel, setPanel] = useState<PanelState>({ active: null, goalId: null });
  const [showArchived, setShowArchived] = useState(false);

  // ---------------------------------------------------------------------------
  // Data fetching and mutations
  // ---------------------------------------------------------------------------
  // TODO: coordinate with backend to paginate this if it becomes a performance issue.
  // For now we want to be able to filter client-side by archived status without refetching.
  const { goals: rawGoals, isGoalsLoading } = useGetListOfGoals(clientId, {
    includeArchived: true,
  });

  const { archiveGoalAction, isArchivingGoal } = useArchiveGoal();
  const { unarchiveGoalAction } = useUnarchiveGoal();
  const { deleteGoalAction, isDeletingGoal } = useDeleteGoal();

  const { data: customerDetails } = useGetClientById(clientId);

  // Apply the archived toggle client-side before any other filtering.
  const visibleGoals = showArchived ? rawGoals : rawGoals.filter((g) => !g.is_archived);
  const archivedCount = rawGoals.filter((g) => g.is_archived).length;
  const activeCount = rawGoals.length - archivedCount;

  const { goals, filterRowProps } = useGoalFilters({
    goals: visibleGoals,
    showArchived,
    onToggleArchived: () => setShowArchived((prev) => !prev),
  });

  const clientName =
    `${customerDetails?.data?.name || ""} ${customerDetails?.data?.child_last_name || ""}`.trim();

  // ---------------------------------------------------------------------------
  // Menu handlers
  // ---------------------------------------------------------------------------
  const handleMenuOpen = (goalId: number) => (e: React.MouseEvent<HTMLButtonElement>) => {
    setGoalMenu({ anchorEl: e.currentTarget, goalId });
  };

  const handleMenuClose = () => setGoalMenu({ anchorEl: null, goalId: null });

  const openPanel = (active: ActivePanel) => {
    // Only allow opening panels if a valid goal is selected
    if (goalMenu.goalId === null) return;
    setPanel({ active, goalId: goalMenu.goalId });
    handleMenuClose();
  };

  const closePanel = () => setPanel({ active: null, goalId: null });

  const handleGoalClick = (goalId: number) => () => {
    history.push(`/clients/${clientId}/goals-targets/${goalId}`);
  };

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------

  const selectedGoal = rawGoals?.find((g) => g.id === panel.goalId) ?? null;
  const selectedGoalId = panel.goalId ?? 0;

  // Goal the overflow menu is currently anchored to — used to gate actions
  const menuGoal = rawGoals?.find((g) => g.id === goalMenu.goalId) ?? null;

  const activeTargets = selectActiveTargetOptions(selectedGoal?.targets);
  const availableGoalsForMove = selectGoalsForMove(rawGoals, selectedGoalId);

  // Empty state conditions
  const goalEmptyState = showArchived ? rawGoals?.length === 0 : activeCount === 0;

  // Render
  return (
    <Stack direction="column" gap={7} p={6}>
      <GoalGridTopRow
        clientName={clientName || "Client"}
        onAddGoal={() => setPanel({ active: "create", goalId: null })}
        // Hide top CTA when empty state is shown to avoid duplicate add actions.
        showAddGoalButton={!goalEmptyState}
      />

      {rawGoals?.length > 0 && <GoalGridFilterRow {...filterRowProps} />}

      <Box component="section" aria-label="Goal cards">
        {!isGoalsLoading && goalEmptyState ? (
          <EmptyState
            variant="purple"
            title="No goals yet — let's add the first one."
            description="Goals anchor the treatment plan and keep progress measurable across sessions."
            actionPlacement="top"
            action={
              <Button
                variant="contained"
                color="primary"
                size="medium"
                startIcon={<MapPinSimpleArea size={16} weight="regular" />}
                onClick={() => setPanel({ active: "create", goalId: null })}
              >
                Add First Goal
              </Button>
            }
          />
        ) : null}

        <Grid container columnSpacing={5} rowSpacing={7}>
          {isGoalsLoading ? (
            <Grid xs={12}>
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                <CircularProgress aria-label="Loading goals" />
              </Box>
            </Grid>
          ) : null}

          {goals.map((goal) => (
            <Grid xs={6} key={goal.id}>
              <GoalCard
                goalName={goal.title}
                status={goal.status}
                domain={goal.domain as GoalDomain}
                behaviorMeasured={goal.behavior_subject ?? "client"}
                goalType={
                  goal.goal_type === "skill-acquisition" ? "skillAcquisition" : "behaviorReduction"
                }
                targetCount={goal.targets.length || 0}
                onMenuOpen={handleMenuOpen(goal.id)}
                chartSlot={
                  <GoalCardMasteryChart
                    goalId={goal.id}
                    status={goal.status}
                    targetCount={goal.targets.length || 0}
                  />
                }
                onClick={handleGoalClick(goal.id)}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Action menu */}
      <GoalActionMenu
        anchorEl={goalMenu.anchorEl}
        open={Boolean(goalMenu.anchorEl)}
        onClose={handleMenuClose}
        onEdit={() => openPanel("edit")}
        onPutOnHold={() => openPanel("putOnHold")}
        onDiscontinue={() => openPanel("discontinue")}
        onArchive={() => openPanel("archive")}
        onUnarchive={() => {
          if (menuGoal) {
            unarchiveGoalAction({ goalId: menuGoal.id, clientId });
          }
          handleMenuClose();
        }}
        onDelete={() => openPanel("delete")}
        isArchived={Boolean(menuGoal?.is_archived)}
      />

      {/* ------------------------------------------------------------------ */}
      {/* Panels                                                               */}
      {/* ------------------------------------------------------------------ */}

      {/* Create Goal */}
      <GoalBuilderPanel
        open={panel.active === "create"}
        onClose={closePanel}
        mode="create"
        clientId={clientId}
      />

      {/* Edit Goal */}
      {selectedGoal && (
        <GoalBuilderPanel
          open={panel.active === "edit"}
          onClose={closePanel}
          mode="edit"
          clientId={clientId}
          goal={selectedGoal}
        />
      )}

      {/* Discontinue */}
      {selectedGoal && (
        <DiscontinueGoalPanel
          open={panel.active === "discontinue"}
          onClose={closePanel}
          goalName={selectedGoal.title}
          goalId={selectedGoalId}
          clientId={clientId}
        />
      )}

      {/* Put On Hold */}
      {selectedGoal && (
        <PutOnHoldPanel
          open={panel.active === "putOnHold"}
          onClose={closePanel}
          goalName={selectedGoal.title}
          goalId={selectedGoalId}
          clientId={clientId}
        />
      )}

      {/* Archive */}
      {selectedGoal && (
        <ArchiveGoalPanel
          open={panel.active === "archive"}
          onClose={closePanel}
          goalName={selectedGoal.title}
          targets={activeTargets}
          availableGoals={availableGoalsForMove}
          isLoading={isArchivingGoal}
          onArchive={(actions: TargetActionState[], rationale: string) => {
            archiveGoalAction(
              {
                goalId: selectedGoalId,
                clientId,
                rationale,
                targetDispositions: buildTargetDispositions(actions, "archive"),
              },
              { onSuccess: closePanel }
            );
          }}
        />
      )}

      {/* Delete */}
      {selectedGoal && (
        <DeleteGoalPanel
          open={panel.active === "delete" && Boolean(selectedGoal?.is_archived)}
          onClose={closePanel}
          goalName={selectedGoal.title}
          targets={activeTargets}
          availableGoals={availableGoalsForMove}
          isLoading={isDeletingGoal}
          onDelete={(actions: TargetActionState[], rationale: string) => {
            deleteGoalAction(
              {
                goalId: selectedGoalId,
                clientId,
                targetDispositions: buildTargetDispositions(actions, "delete"),
                rationale,
              },
              { onSuccess: closePanel }
            );
          }}
        />
      )}
    </Stack>
  );
};

export default ListOfGoals;
